import {
  CheckCircleOutlined,
  EditOutlined,
  ExperimentOutlined,
  EyeOutlined,
  MinusCircleOutlined,
} from '@ant-design/icons'
import { Stack } from '@sureco/design-system'
import {
  Button,
  Tag,
  Tooltip,
  Typography,
  notification,
} from '@surecompanies/core_components'
import { useQuery } from '@tanstack/react-query'
import axios from 'axios'
import { listDemoCompanies } from '../../../DemoCustomer/api/demo-customer.api'
import React, { useEffect, useState } from 'react'

export const IconStatusMapper: Record<
  string,
  { color: string; text: string; icon: React.ReactElement }
> = {
  active: {
    color: '#98CE24',
    text: '#fff',
    icon: <CheckCircleOutlined />,
  },
  draft: {
    color: '#FAAD14',
    text: '#fff',
    icon: <EditOutlined />,
  },
  inactive: { color: '#999', text: '#fff', icon: <MinusCircleOutlined /> },
}

export type DemoCompanyListResult = {
  id: string
  employee_count: number
  status: string
  name: string
}

type UseListDemoCompanyProps = {
  onView: (record: DemoCompanyListResult) => void
  onEdit: (record: DemoCompanyListResult) => void
  page?: number
  limit?: number
  searchTerm?: string
}

type DemoCompanyListResultDTO = {
  data: DemoCompanyListResult[]
  meta: {
    total: number
    page: number
    limit: number
  }
}

export default function useListDemoCompany({
  onView,
  onEdit,
  page = 1,
  limit = 200,
  searchTerm = '',
}: UseListDemoCompanyProps) {
  const [cancelToken, setCancelToken] = useState<any>(null)

  // The demo endpoint scopes results to demo companies itself and prepends
  // the "ZZ - DEMO - " prefix to this filter, so pass the raw search text.
  const nameFilter = searchTerm?.trim() ?? ''

  async function fetchDemoCompanies(): Promise<DemoCompanyListResultDTO> {
    try {
      if (cancelToken) {
        cancelToken.cancel('Request cancelled due to new search')
      }

      const source = axios.CancelToken.source()
      setCancelToken(source)

      return await listDemoCompanies(
        {
          page,
          limit,
          name: nameFilter || undefined,
          sortBy: 'name',
          sortOrder: 'asc',
        },
        { cancelToken: source.token }
      )
    } catch (err) {
      if (axios.isCancel && axios.isCancel(err)) {
        throw err
      }

      notification.error({
        message: 'Something went wrong loading demo companies',
      })

      return {
        data: [],
        meta: { total: 0, page, limit },
      }
    }
  }

  const { isPending, data, refetch } = useQuery({
    queryKey: ['getDemoCompanies', page, limit, nameFilter],
    queryFn: fetchDemoCompanies,
    staleTime: 0,
    enabled: true,
    retry: (failureCount, error) => {
      if (axios.isCancel && axios.isCancel(error)) return false
      return failureCount < 3
    },
  })

  const columns = [
    {
      title: 'Status',
      dataIndex: 'status',
      render: (_: unknown, { status }: DemoCompanyListResult) => {
        const config =
          IconStatusMapper[status?.toLowerCase()] ?? IconStatusMapper['draft']
        return (
          <Tag
            icon={config.icon}
            color={config.color}
            style={{ borderRadius: 18 }}
          >
            <Typography.Text
              type={config.color as any}
              style={{ color: config.text, fontSize: 14, fontWeight: 'bolder' }}
            >
              {status}
            </Typography.Text>
          </Tag>
        )
      },
    },
    {
      title: 'Company Name',
      dataIndex: 'name',
      render: (name: string) => {
        // Strip the internal prefix for display; keep the demo badge.
        const displayName = name.replace(/^ZZ - DEMO - /i, '')
        return (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              background: '#fff7e6',
              border: '1px solid #ffd591',
              borderRadius: 4,
              padding: '2px 8px',
              color: '#873800',
            }}
          >
            <ExperimentOutlined style={{ color: '#fa8c16', flexShrink: 0 }} />
            {displayName}
          </span>
        )
      },
    },
    {
      title: 'Employees',
      dataIndex: 'employee_count',
    },
    {
      title: 'Action',
      key: 'action',
      render: (_: unknown, record: DemoCompanyListResult) => (
        <Stack direction="row" space={10}>
          <Tooltip title="View">
            <Button
              type="primary"
              shape="circle"
              icon={<EyeOutlined />}
              onClick={() => onView(record)}
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button
              shape="circle"
              icon={<EditOutlined />}
              onClick={() => onEdit(record)}
            />
          </Tooltip>
        </Stack>
      ),
    },
  ]

  useEffect(() => {
    refetch()
  }, [page, limit, nameFilter, refetch])

  useEffect(() => {
    return () => {
      if (cancelToken) {
        cancelToken.cancel('Component unmounted')
      }
    }
  }, [cancelToken])

  return {
    dataSource: data?.data ?? [],
    total: data?.meta?.total ?? 0,
    isLoading: isPending,
    columns,
    refetch,
  }
}
